/* eslint-disable no-param-reassign */ /* eslint-disable no-restricted-syntax */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { compareNodeOrder, getChildNodes, getFirstItem } from '../collections';
import { Selection } from './Selection';
/**
 * An interface for reading and updating multiple selection state.
 */ export class SelectionManager {
    /**
   * The type of selection that is allowed in the collection.
   */ get selectionMode() {
        return this.state.selectionMode;
    }
    /**
   * Whether the collection allows empty selection.
   */ get disallowEmptySelection() {
        return this.state.disallowEmptySelection;
    }
    /**
   * The selection behavior for the collection.
   */ get selectionBehavior() {
        return this.state.selectionBehavior;
    }
    /**
   * Sets the selection behavior for the collection.
   */ setSelectionBehavior(selectionBehavior) {
        this.state.setSelectionBehavior(selectionBehavior);
    }
    /**
   * Whether the collection is currently focused.
   */ get isFocused() {
        return this.state.isFocused;
    }
    /**
   * Sets whether the collection is focused.
   */ setFocused(isFocused) {
        this.state.setFocused(isFocused);
    }
    /**
   * The current focused key in the collection.
   */ get focusedKey() {
        return this.state.focusedKey;
    }
    /** Whether the first or last child of the focused key should receive focus. */ get childFocusStrategy() {
        return this.state.childFocusStrategy;
    }
    /**
   * Sets the focused key.
   */ setFocusedKey(key, childFocusStrategy) {
        if (key == null || this.collection.getItem(key)) {
            this.state.setFocusedKey(key, childFocusStrategy);
        }
    }
    /**
   * The currently selected keys in the collection.
   */ get selectedKeys() {
        return this.state.selectedKeys;
    }
    /**
   * The raw selection value for the collection.
   * Either 'all' for select all, or a set of keys.
   */ get rawSelection() {
        return this.state.selectedKeys;
    }
    /**
   * Returns whether a key is selected.
   */ isSelected(key) {
        var _this_state_selectedKeys;
        if (this.state.selectionMode === 'none') {
            return false;
        }
        key = this.getKey(key);
        return ((_this_state_selectedKeys = this.state.selectedKeys) === null || _this_state_selectedKeys === void 0 ? void 0 : _this_state_selectedKeys.has(key)) || false;
    }
    /**
   * Whether the selection is empty.
   */ get isEmpty() {
        var _this_state_selectedKeys;
        return ((_this_state_selectedKeys = this.state.selectedKeys) === null || _this_state_selectedKeys === void 0 ? void 0 : _this_state_selectedKeys.size) === 0;
    }
    /**
   * Whether all items in the collection are selected.
   */ get isSelectAll() {
        if (this.isEmpty) {
            return false;
        }
        if (this._isSelectAll != null) {
            return this._isSelectAll;
        }
        const allKeys = this.getSelectAllKeys();
        const selectedKeys = this.state.selectedKeys;
        this._isSelectAll = allKeys.every((k)=>selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.has(k));
        return this._isSelectAll;
    }
    get firstSelectedKey() {
        let first = null;
        if (!this.state.selectedKeys) {
            return null;
        }
        for (const key of this.state.selectedKeys){
            const item = this.collection.getItem(key);
            if (!first || item && compareNodeOrder(this.collection, item, first) < 0) {
                first = item;
            }
        }
        return (first === null || first === void 0 ? void 0 : first.key) || null;
    }
    get lastSelectedKey() {
        let last = null;
        if (!this.state.selectedKeys) {
            return null;
        }
        for (const key of this.state.selectedKeys){
            const item = this.collection.getItem(key);
            if (!last || item && compareNodeOrder(this.collection, item, last) > 0) {
                last = item;
            }
        }
        return (last === null || last === void 0 ? void 0 : last.key) || null;
    }
    get disabledKeys() {
        return this.state.disabledKeys;
    }
    get disabledBehavior() {
        return this.state.disabledBehavior;
    }
    /**
   * Extends the selection to the given key.
   */ extendSelection(toKey) {
        if (this.selectionMode === 'none') {
            return;
        }
        if (this.selectionMode === 'single') {
            this.replaceSelection(toKey);
            return;
        }
        toKey = this.getKey(toKey);
        const selectedKeys = this.state.selectedKeys;
        const anchorKey = (selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.anchorKey) || toKey;
        const selection = new Selection(selectedKeys, anchorKey, toKey);
        for (const key of this.getKeyRange(anchorKey, (selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.currentKey) || toKey)){
            selection.delete(key);
        }
        for (const key of this.getKeyRange(toKey, anchorKey)){
            if (this.canSelectItem(key)) {
                selection.add(key);
            }
        }
        this.state.setSelectedKeys(selection);
    }
    getKeyRange(from, to) {
        const fromItem = this.collection.getItem(from);
        const toItem = this.collection.getItem(to);
        if (fromItem && toItem) {
            if (compareNodeOrder(this.collection, fromItem, toItem) <= 0) {
                return this.getKeyRangeInternal(from, to);
            }
            return this.getKeyRangeInternal(to, from);
        }
        return [];
    }
    getKeyRangeInternal(from, to) {
        const keys = [];
        let key = from;
        while(key){
            const item = this.collection.getItem(key);
            if (item && item.type === 'item' || (item === null || item === void 0 ? void 0 : item.type) === 'cell' && this.allowsCellSelection) {
                keys.push(key);
            }
            if (key === to) {
                return keys;
            }
            key = this.collection.getKeyAfter(key);
        }
        return [];
    }
    getKey(key) {
        let item = this.collection.getItem(key);
        if (!item) {
            // ¯\_(ツ)_/¯
            return key;
        }
        // If cell selection is allowed, just return the key.
        if (item.type === 'cell' && this.allowsCellSelection) {
            return key;
        }
        // Find a parent item to select
        while((item === null || item === void 0 ? void 0 : item.type) !== 'item' && (item === null || item === void 0 ? void 0 : item.parentKey) != null){
            item = this.collection.getItem(item === null || item === void 0 ? void 0 : item.parentKey);
        }
        if (!item || item.type !== 'item') {
            return null;
        }
        return item.key;
    }
    /**
   * Toggles whether the given key is selected.
   */ toggleSelection(key) {
        if (this.selectionMode === 'none') {
            return;
        }
        if (this.selectionMode === 'single' && !this.isSelected(key)) {
            this.replaceSelection(key);
            return;
        }
        key = this.getKey(key);
        if (key == null) {
            return;
        }
        const keys = new Selection(this.state.selectedKeys);
        if (keys.has(key)) {
            keys.delete(key);
        // TODO: move anchor to last selected key...
        // Does `current` need to move here too?
        } else if (this.canSelectItem(key)) {
            keys.add(key);
            keys.anchorKey = key;
            keys.currentKey = key;
        }
        if (this.disallowEmptySelection && keys.size === 0) {
            return;
        }
        this.state.setSelectedKeys(keys);
    }
    /**
   * Replaces the selection with only the given key.
   */ replaceSelection(key) {
        if (this.selectionMode === 'none') {
            return;
        }
        key = this.getKey(key);
        if (key == null) {
            return;
        }
        const selection = this.canSelectItem(key) ? new Selection([
            key
        ], key, key) : new Selection();
        this.state.setSelectedKeys(selection);
    }
    /**
   * Replaces the selection with the given keys.
   */ setSelectedKeys(keys) {
        if (this.selectionMode === 'none') {
            return;
        }
        const selection = new Selection();
        for (let key of keys){
            key = this.getKey(key);
            if (key != null) {
                selection.add(key);
                if (this.selectionMode === 'single') {
                    break;
                }
            }
        }
        this.state.setSelectedKeys(selection);
    }
    getSelectAllKeys() {
        const keys = [];
        const addKeys = (key)=>{
            while(key){
                if (this.canSelectItem(key)) {
                    const item = this.collection.getItem(key);
                    if ((item === null || item === void 0 ? void 0 : item.type) === 'item') {
                        keys.push(key);
                    }
                    // Add child keys. If cell selection is allowed, then include item children too.
                    if ((item === null || item === void 0 ? void 0 : item.hasChildNodes) && (this.allowsCellSelection || (item === null || item === void 0 ? void 0 : item.type) !== 'item')) {
                        var _getFirstItem;
                        const firstItemKey = (_getFirstItem = getFirstItem(getChildNodes(item, this.collection))) === null || _getFirstItem === void 0 ? void 0 : _getFirstItem.key;
                        if (firstItemKey) {
                            addKeys(firstItemKey);
                        }
                    }
                }
                key = this.collection.getKeyAfter(key);
            }
        };
        const firstKey = this.collection.getFirstKey();
        if (firstKey) {
            addKeys(firstKey);
        }
        return keys;
    }
    /**
   * Selects all items in the collection.
   */ selectAll() {
        if (!this.isSelectAll && this.selectionMode === 'multiple') {
            const allElements = this.getSelectAllKeys();
            this.state.setSelectedKeys(new Selection(allElements));
        }
    }
    /**
   * Removes all keys from the selection.
   */ clearSelection() {
        if (!this.disallowEmptySelection && this.state.selectedKeys && this.state.selectedKeys.size > 0) {
            this.state.setSelectedKeys(new Selection());
        }
    }
    /**
   * Toggles between select all and an empty selection.
   */ toggleSelectAll() {
        if (this.isSelectAll) {
            this.clearSelection();
        } else {
            this.selectAll();
        }
    }
    select(key, e) {
        if (this.selectionMode === 'none') {
            return;
        }
        if (this.selectionMode === 'single') {
            if (this.isSelected(key) && !this.disallowEmptySelection) {
                this.toggleSelection(key);
            } else {
                this.replaceSelection(key);
            }
        } else if (this.selectionBehavior === 'toggle' || e && (e.pointerType === 'touch' || e.pointerType === 'virtual')) {
            // if touch or virtual (VO) then we just want to toggle, otherwise it's impossible to multi select because they don't have modifier keys
            this.toggleSelection(key);
        } else {
            this.replaceSelection(key);
        }
    }
    /**
   * Returns whether the current selection is equal to the given selection.
   */ isSelectionEqual(selection) {
        if (selection === this.state.selectedKeys) {
            return true;
        }
        // Check if the set of keys match.
        const selectedKeys = this.selectedKeys;
        if (selection.size !== (selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.size)) {
            return false;
        }
        for (const key of selection){
            if (!(selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.has(key))) {
                return false;
            }
        }
        for (const key of selectedKeys){
            if (!selection.has(key)) {
                return false;
            }
        }
        return true;
    }
    canSelectItem(key) {
        if (this.state.selectionMode === 'none' || this.state.disabledKeys.has(key)) {
            return false;
        }
        const item = this.collection.getItem(key);
        if (!item || item.type === 'cell' && !this.allowsCellSelection) {
            return false;
        }
        return true;
    }
    isDisabled(key) {
        return this.state.disabledKeys.has(key) && this.state.disabledBehavior === 'all';
    }
    isLink(key) {
        var _this_collection_getItem_props, _this_collection_getItem;
        return !!((_this_collection_getItem = this.collection.getItem(key)) === null || _this_collection_getItem === void 0 ? void 0 : (_this_collection_getItem_props = _this_collection_getItem.props) === null || _this_collection_getItem_props === void 0 ? void 0 : _this_collection_getItem_props.href);
    }
    constructor(collection, state, options){
        _define_property(this, "collection", void 0);
        _define_property(this, "state", void 0);
        _define_property(this, "allowsCellSelection", void 0);
        _define_property(this, "_isSelectAll", void 0);
        this.collection = collection;
        this.state = state;
        var _options_allowsCellSelection;
        this.allowsCellSelection = (_options_allowsCellSelection = options === null || options === void 0 ? void 0 : options.allowsCellSelection) !== null && _options_allowsCellSelection !== void 0 ? _options_allowsCellSelection : false;
        this._isSelectAll = null;
    }
}

//# sourceMappingURL=SelectionManager.js.map